import CircularProgress from "@mui/material/CircularProgress";

interface OnboardingSetupStepProps {
  firstName: string;
  lastName: string;
  orgName: string;
  error: string | null;
  onRetry: () => void;
}

/**
 * The stand-in shown while the client's organization is created.
 *
 * Picking a plan is the first moment the wizard has real work to do — an organization, a root
 * group, billing terms, a membership and a Clerk profile sync. That work used to happen with no
 * screen of its own: Starter ran it behind an unchanged "Continue" button, and Pro ran it behind
 * an empty payment drawer whose grey placeholder boxes read as a hang rather than as progress.
 *
 * So it gets a screen. Naming the client and their organization is the point — a spinner alone
 * says "wait", this says what is being built and for whom.
 *
 * A failure lands HERE, where the work happened, with the server's own sentence and a retry —
 * rather than surfacing two screens later against a form that has nothing to do with it.
 */
export function OnboardingSetupStep({
  firstName,
  lastName,
  orgName,
  error,
  onRetry,
}: OnboardingSetupStepProps) {
  const fullName = `${firstName} ${lastName}`.trim();

  if (error) {
    return (
      <div className="gfg-ob-panel-in">
        <h1 className="gfg-auth-title">We couldn't finish setting up</h1>
        <p className="gfg-auth-sub" role="alert">
          {error}
        </p>
        <button type="button" className="gfg-auth-btn" onClick={onRetry}>
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="gfg-ob-panel-in gfg-ob-setup" role="status" aria-live="polite">
      <CircularProgress
        size={34}
        thickness={3}
        aria-hidden="true"
        sx={{ color: "var(--color-dark-dust)" }}
      />
      <h1 className="gfg-auth-title">{fullName ? `Welcome, ${fullName}.` : "Welcome."}</h1>
      <p className="gfg-auth-sub">
        Getting things ready for {orgName.trim() || "your organization"}…
      </p>
    </div>
  );
}
